<script lang="ts" setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import router from "@/router/index";
import { GetEnergyCompassData, severityCode } from "@/services/api/chart.api";
import energyCompassImage from "@/assets/img/energiekompas.png";
import indicativeLabelIcon from "@/assets/img/indicativeLabelIcon.svg";
import { useUserStore } from "@/stores/user";

const userStore = useUserStore();

const props = defineProps<{
  token?: any;
  refDate?: any;
}>();

const canvasRef = ref<HTMLCanvasElement | null>(null);

// redraw scheduling to avoid excessive work on mousemove/resize
let pendingRAF = false;
function scheduleRedraw() {
  if (pendingRAF) return;
  pendingRAF = true;
  requestAnimationFrame(() => {
    pendingRAF = false;
    redraw();
  });
}
const points = ref<any[]>();
const currentPoint = ref<any>(null);
const svgDimension = [18, 18];
// Reactive tooltip state (replaces DOM tooltip element)
const tooltipVisible = ref(false);
const tooltipHtml = ref("");
const tooltipX = ref(0);
const tooltipY = ref(0);

// Helper: point-in-triangle test (barycentric technique)
function pointInTriangle(pointX: number, pointY: number, apexX: number, apexY: number, baseLeftX: number, baseLeftY: number, baseRightX: number, baseRightY: number): boolean {
  const v0x = baseRightX - apexX;
  const v0y = baseRightY - apexY;
  const v1x = baseLeftX - apexX;
  const v1y = baseLeftY - apexY;
  const v2x = pointX - apexX;
  const v2y = pointY - apexY;

  const dot00 = v0x * v0x + v0y * v0y;
  const dot01 = v0x * v1x + v0y * v1y;
  const dot02 = v0x * v2x + v0y * v2y;
  const dot11 = v1x * v1x + v1y * v1y;
  const dot12 = v1x * v2x + v1y * v2y;

  const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);
  const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
  const v = (dot00 * dot12 - dot01 * dot02) * invDenom;

  return u >= 0 && v >= 0 && u + v <= 1;
}

// Drawing helpers
function drawCircle(ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, fillColor: string | null, strokeColor: string | null, strokeWidth: number): void {
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
  if (fillColor) {
    ctx.fillStyle = fillColor;
    ctx.fill();
  }
  if (strokeColor && strokeWidth) {
    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = strokeColor;
    ctx.stroke();
  }
}

function drawTriangleUp(ctx: CanvasRenderingContext2D, centerX: number, centerY: number, width: number, height: number, fillColor: string | null, strokeColor: string | null, strokeWidth: number): void {
  ctx.beginPath();
  ctx.moveTo(centerX, centerY - height);
  ctx.lineTo(centerX - width, centerY + height / 2);
  ctx.lineTo(centerX + width, centerY + height / 2);
  ctx.closePath();
  if (fillColor) {
    ctx.fillStyle = fillColor;
    ctx.fill();
  }
  if (strokeColor && strokeWidth) {
    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = strokeColor;
    ctx.stroke();
  }
}

function drawTriangleDown(ctx: CanvasRenderingContext2D, centerX: number, centerY: number, width: number, height: number, fillColor: string | null, strokeColor: string | null, strokeWidth: number): void {
  ctx.beginPath();
  ctx.moveTo(centerX, centerY + height);
  ctx.lineTo(centerX - width, centerY - height / 2);
  ctx.lineTo(centerX + width, centerY - height / 2);
  ctx.closePath();
  if (fillColor) {
    ctx.fillStyle = fillColor;
    ctx.fill();
  }
  if (strokeColor && strokeWidth) {
    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = strokeColor;
    ctx.stroke();
  }
}

function drawIcon(ctx: CanvasRenderingContext2D, img: HTMLImageElement | null, centerX: number, centerY: number, width: number, height: number): void {
  if (!img) return;
  ctx.drawImage(img, centerX - width / 2, centerY - height / 2, width, height);
}

function drawHoverSquare(ctx: CanvasRenderingContext2D, centerX: number, centerY: number, width: number, height: number): void {
  // white under-stroke for contrast
  ctx.lineWidth = HOVER_WHITE_STROKE;
  ctx.strokeStyle = "#ffffff";
  ctx.strokeRect(centerX - width / 2, centerY - height / 2, width, height);
  // black thin outline
  ctx.lineWidth = HOVER_BLACK_STROKE;
  ctx.strokeStyle = "#000000";
  ctx.strokeRect(centerX - width / 2, centerY - height / 2, width, height);
}

// Preload images to avoid flicker when redrawing
const energyImg = new Image();
let energyImgLoaded = false;
energyImg.src = energyCompassImage;
energyImg.onload = () => {
  energyImgLoaded = true;
  if (canvasRef.value) scheduleRedraw();
};
energyImg.onerror = () => {
  energyImgLoaded = false;
};

const indicativeImg = new Image();
let indicativeImgLoaded = false;
indicativeImg.src = indicativeLabelIcon;
indicativeImg.onload = () => {
  indicativeImgLoaded = true;
  if (canvasRef.value) scheduleRedraw();
};
indicativeImg.onerror = () => {
  indicativeImgLoaded = false;
};

// Visual constants
const labelColors = ["#e51c20", "#eb5d19", "#f6a210", "#fce510", "#a6c92b", "#53b039", "#029246", "#029246", "#029246", "#029246", "#029246"];
const labelLabels = ["G", "F", "E", "D", "C", "B", "A", "A+", "A++", "A+++", "A++++", "A+++++"];

const categoryCutoffs = [108, 100, 60, 35, 25, 18, 0, -14];
const categoryColors = ["#ff0303", "#d90000", "#b21d45", "#b21d9c", "#821d9c", "#431d9c", "#002f73"];
const categoryLabels = ["zeer onzuinig", "onzuinig", "gemiddeld", "zuinig", "zeer zuinig", "Paris Proof", "WENG"];

// Tweakable geometry constants
const ICON_PADDING = 2; // padding around icon for hover box
const HOVER_WHITE_STROKE = 3.2;
const HOVER_BLACK_STROKE = 1.2;
const TRIANGLE_WIDTH_FACTOR = 1.2;
const TRIANGLE_HEIGHT_FACTOR = 1.8;

const legend = [
  {
    label: "EP-online label",
    icon: "circle",
    color: "#bbb",
  },
  {
    label: "Buiten de chart",
    icon: "triangle",
    color: "#bbb",
  },
  {
    label: "Indicatief label",
    icon: "square",
    color: "#bbb",
  },
];

const margin = 5; // margin between boxes (double margin between cat & lbl boxes)
const dotSize = 6;

watch(
  () => points.value,
  () => {
    scheduleRedraw();
  },
  { immediate: false }
);

watch(
  () => props.token,
  () => {
    fetchData();
  },
  { immediate: true }
);

async function fetchData() {
  let response = await GetEnergyCompassData(props.token);
  console.log("EnergyCompassData fetchData, response: ", response);
  if (response && response.severity < severityCode.warning) {
    let newPoints = response.data.items;
    if (newPoints == null) {
      newPoints = [];
    } else {
      for (let i = 0; i < newPoints.length; i++) {
        newPoints[i].x = newPoints[i].normalizedEPOnlineLabel != null ? newPoints[i].normalizedEPOnlineLabel : newPoints[i].normalizedIndicativeLabel;
        newPoints[i].y = newPoints[i].normalizedWeiiScore;
      }
    }
    points.value = newPoints;
  } else {
    points.value = undefined;
  }
}

onMounted(() => {
  window.addEventListener("resize", handleResize);
  handleResize();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

function handleResize() {
  // canvasRef is bound via template ref; just schedule a redraw when size changes
  scheduleRedraw();
}

async function doClick() {
  if (currentPoint.value) {
    router.push({ name: "BuildingDetails", params: { partyReference: userStore.currentPartyReference, addressId: currentPoint.value.paoId } });
  }
}

function doMove(event: MouseEvent): void {
  if (!points.value) {
    doLeave();
    return;
  }

  if (!canvasRef.value) return;

  // Always use the canvas bounding rect so overlays (like the tooltip) do not interfere with pointer coordinates
  let rect = canvasRef.value.getBoundingClientRect();
  let mouseX = event.clientX - rect.left;
  let mouseY = event.clientY - rect.top;

  let canvasWidth = canvasRef.value.clientWidth;
  let canvasHeight = canvasRef.value.clientHeight;
  let boxSize = (canvasWidth - margin) / 13; // box size (category box is double width)
  let categoryScale = (canvasHeight - boxSize - margin) / (categoryCutoffs[0] - categoryCutoffs[7]);

  let foundIndices = [];
  for (let i = points.value.length - 1; i >= 0; i--) {
    let point = points.value[i];
    let centerX = point.x * boxSize + (2 * boxSize + (boxSize + margin) / 2);
    // compute same clamping + shape as redraw so positions match exactly
    let shape = 0;
    let yy = 0;
    if (point.y < 0) {
      yy = 0;
      shape = 1;
    } else if (point.y > categoryCutoffs[0]) {
      yy = categoryCutoffs[0];
      shape = 2;
    } else {
      yy = point.y;
      shape = 0;
    }
    let centerY = (categoryCutoffs[0] - yy) * categoryScale;

    // hit-testing: shape-aware
    let hit = false;
    if (shape === 0) {
      // circle
      let hitRadius = Math.max(dotSize * 1.2, 6);
      if (Math.hypot(mouseX - centerX, mouseY - centerY) <= hitRadius) hit = true;
    } else {
      // triangle: build the same triangle vertices used in redraw
      if (shape === 1) {
        // triangle up: apex above, base below
        var apexX = centerX;
        var apexY = centerY - dotSize * 1.8;
        var baseLeftX = centerX - dotSize * 1.2;
        var baseLeftY = centerY + (dotSize * 1.8) / 2;
        var baseRightX = centerX + dotSize * 1.2;
        var baseRightY = centerY + (dotSize * 1.8) / 2;
      } else {
        // triangle down: apex below, base above
        var apexX = centerX;
        var apexY = centerY + dotSize * 1.8;
        var baseLeftX = centerX - dotSize * 1.2;
        var baseLeftY = centerY - (dotSize * 1.8) / 2;
        var baseRightX = centerX + dotSize * 1.2;
        var baseRightY = centerY - (dotSize * 1.8) / 2;
      }
      if (pointInTriangle(mouseX, mouseY, apexX, apexY, baseLeftX, baseLeftY, baseRightX, baseRightY)) hit = true;

      // also accept clicks near the indicative icon bounding circle
      if (!hit && point.normalizedEPOnlineLabel == null) {
        let imgHalf = Math.max(svgDimension[0], svgDimension[1]) / 2 + 4;
        if (Math.hypot(mouseX - centerX, mouseY - centerY) <= imgHalf) hit = true;
      }
    }

    if (hit) foundIndices.push(i);
  }

  if (foundIndices.length == 0) {
    doLeave();
    return;
  }

  tooltipVisible.value = true;
  // The click event is triggered at the cursor's 0,0 position.
  // When the popup is active, clicks target the popup instead of the intended circle element.
  // Adding +1 to style.left ensures the tooltip does not use this position, allowing the onclick event to be correctly triggered.
  // Applying an onclick event directly to a specific icon is not feasible in this context.
  // Alternatively, you would need to verify the mouse coordinates relative to the circle or square, or integrate the popup within the canvas itself.  tooltip.style.left = event.pageX + 1 + "px";
  const compassEl = canvasRef.value?.closest('.energy-compass') as HTMLElement | null;
  if (compassEl) tooltipY.value = event.clientY - compassEl.getBoundingClientRect().top - 40;
  tooltipHtml.value = "";

  for (let i = 0; i < foundIndices.length; i++) {
    tooltipHtml.value += points.value[foundIndices[i]].description;
    tooltipHtml.value += "<br/>";
    tooltipHtml.value += "WEii-score: " + points.value[foundIndices[i]].weiiScore.toFixed(0) + " kWh/m²";

    if (i < foundIndices.length - 1) {
      tooltipHtml.value += "<br/><br/>";
    }
  }
  currentPoint.value = points.value[foundIndices[0]];
  if (canvasRef.value) canvasRef.value.style.cursor = "pointer";
  scheduleRedraw();
  tooltipVisible.value = true;
  const compassEl2 = canvasRef.value?.closest('.energy-compass') as HTMLElement | null;
  if (compassEl2) tooltipX.value = event.clientX - compassEl2.getBoundingClientRect().left + 10;
}

function doLeave() {
  tooltipVisible.value = false;
  currentPoint.value = null;
  if (canvasRef.value) canvasRef.value.style.cursor = "default";
  scheduleRedraw();
}

function onCanvasKeyDown(evt: KeyboardEvent): void {
  if (!points.value || points.value.length === 0) return;
  let idx = -1;
  if (currentPoint.value) {
    idx = points.value.indexOf(currentPoint.value);
  }
  if (evt.key === "ArrowRight" || evt.key === "ArrowDown") {
    idx = Math.min(points.value.length - 1, Math.max(0, idx + 1));
    currentPoint.value = points.value[idx];
    scheduleRedraw();
    evt.preventDefault();
  } else if (evt.key === "ArrowLeft" || evt.key === "ArrowUp") {
    idx = Math.max(0, idx - 1);
    currentPoint.value = points.value[idx];
    scheduleRedraw();
    evt.preventDefault();
  } else if (evt.key === "Enter") {
    if (currentPoint.value) doClick();
    evt.preventDefault();
  }
}

function redraw() {
  if (!canvasRef.value) return;
  const ratio = window.devicePixelRatio || 1;
  const ctx = canvasRef.value.getContext("2d");
  if (!ctx) return;
  let cssW = canvasRef.value.clientWidth;
  let cssH = canvasRef.value.clientHeight;
  // scale for high DPI displays
  canvasRef.value.width = Math.max(1, Math.round(cssW * ratio));
  canvasRef.value.height = Math.max(1, Math.round(cssH * ratio));
  canvasRef.value.style.width = cssW + "px";
  canvasRef.value.style.height = cssH + "px";
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  let canvasWidth = cssW;
  let canvasHeight = cssH;

  let categoryFont = "bold 10px arial";

  let boxSize = (canvasWidth - margin) / 13; // box size (category box is double width)

  let categoryScale = (canvasHeight - boxSize - margin) / (categoryCutoffs[0] - categoryCutoffs[7]);

  // achtergrond-rechthoek, nog zonder rand
  let x1 = 2 * boxSize;
  let x2 = canvasWidth;
  let y1 = 0;
  let y2 = (categoryCutoffs[0] - categoryCutoffs[7]) * categoryScale;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x1, y2);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x2, y1);
  ctx.closePath();
  ctx.lineWidth = 1;

  for (let i = 0; i < 7; i++) {
    //WEii labels
    let bx = margin;
    let by = (categoryCutoffs[0] - categoryCutoffs[i]) * categoryScale + margin;
    let bw = 2 * (boxSize - margin);
    let bh = (categoryCutoffs[i] - categoryCutoffs[i + 1]) * categoryScale - margin;
    roundRect(ctx, bx, by - margin / 2, bw, bh, margin);
    ctx.fillStyle = categoryColors[i];
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = categoryFont;
    centeredText(ctx, categoryLabels[i], bx + bw / 2, by + (bh - margin) / 2);
    if (i > 0) {
      //horizontal lines
      ctx.beginPath();
      ctx.moveTo(x1, by - margin);
      ctx.lineTo(x2, by - margin);
      ctx.strokeStyle = "#d0d0d0";
      ctx.stroke();
    }
  }


  // Draw diagonal 'ideal' line
  // x1, y1, x2, y2 are already defined above for the chart area
  // Use canvasHeight for height
  ctx.beginPath();
  ctx.lineWidth = 3;
  ctx.moveTo(x1, y1 + canvasHeight / 100 * 44);
  ctx.lineTo(x2, y2 - canvasHeight / 100 * 18);
  ctx.strokeStyle = '#d0d0d0';
  ctx.stroke();
  ctx.lineWidth = 1;

  //Energiekompas image (use preloaded image to avoid flicker)
  if (energyImgLoaded) {
    ctx.drawImage(energyImg, 130, 25, 183, 53);
  }

  for (let i = 0; i < 11; i++) {
    //Energy labels
    let bx = (i + 2) * boxSize + margin;
    let by = canvasHeight - boxSize;
    let bw = boxSize - margin;
    let bh = boxSize - margin;
    roundRect(ctx, bx, by, bw, bh, margin);
    ctx.fillStyle = labelColors[i];
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    centeredLabel(ctx, labelLabels[i], bx + bw / 2, by + bh / 2, "24px arial", "bold 10px arial");
  }

  // de puntenwolk
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x1, y2);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x2, y1);
  ctx.closePath();
  ctx.save();
  ctx.clip();

  if (points.value) {
    for (let i = 0; i < points.value.length; i++) {
      let point = points.value[i];
      if (point.normalizedEPOnlineLabel != null || point.normalizedIndicativeLabel != null) {
        let centerX = point.x * boxSize + (2 * boxSize + (boxSize + margin) / 2);
        let shape = 0;
        let yy = 0;
        if (point.y < 0) {
          yy = 0;
          shape = 1;
        } else if (point.y > categoryCutoffs[0]) {
          yy = categoryCutoffs[0];
          shape = 2;
        } else {
          yy = point.y;
        }
        let centerY = (categoryCutoffs[0] - yy) * categoryScale;
        ctx.beginPath();

        switch (shape) {
          case 0:
            drawCircle(ctx, centerX, centerY, dotSize, null, null, 0);
            break;
          case 1:
            drawTriangleUp(ctx, centerX, centerY, dotSize * TRIANGLE_WIDTH_FACTOR, dotSize * TRIANGLE_HEIGHT_FACTOR, null, null, 0);
            break;
          case 2:
            drawTriangleDown(ctx, centerX, centerY, dotSize * TRIANGLE_WIDTH_FACTOR, dotSize * TRIANGLE_HEIGHT_FACTOR, null, null, 0);
            break;
        }

          if (point.normalizedEPOnlineLabel == null) {
            // For indicative icons do not draw a background; draw only the icon image
            if (indicativeImgLoaded) {
              drawIcon(ctx, indicativeImg, centerX, centerY, svgDimension[0], svgDimension[1]);
            }
          } else {
          let colorIndex = Math.floor(point.x + 0.5);
          if (colorIndex >= 0 && colorIndex < 11) ctx.fillStyle = labelColors[colorIndex];
          else ctx.fillStyle = "#000000";
          // fill the shape and stroke with a thin white border to separate overlapping points
          ctx.fill();
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = "#ffffff";
          ctx.stroke();
        }
        // hover highlight for the active point (shape-matching)
        if (currentPoint.value && currentPoint.value === point) {
          ctx.save();
          ctx.beginPath();
          // If this point uses the indicative icon, draw a smaller square outline matching the icon bounds
          if (point.normalizedEPOnlineLabel == null) {
            const wBox = svgDimension[0] + ICON_PADDING * 2;
            const hBox = svgDimension[1] + ICON_PADDING * 2;
            drawHoverSquare(ctx, centerX, centerY, wBox, hBox);
          } else if (shape === 0) {
            let hoverRadius = dotSize * 1.4;
            drawCircle(ctx, centerX, centerY, hoverRadius, null, "#000000", 1.2);
          } else if (shape === 1) {
            let w2 = dotSize * 1.2 * 1.6;
            let h2 = dotSize * 1.8 * 1.4;
            ctx.moveTo(centerX, centerY - h2);
            ctx.lineTo(centerX - w2, centerY + h2 / 2);
            ctx.lineTo(centerX + w2, centerY + h2 / 2);
            ctx.closePath();
          } else {
            let w2 = dotSize * 1.2 * 1.6;
            let h2 = dotSize * 1.8 * 1.4;
            ctx.moveTo(centerX, centerY + h2);
            ctx.lineTo(centerX - w2, centerY - h2 / 2);
            ctx.lineTo(centerX + w2, centerY - h2 / 2);
            ctx.closePath();
          }
          ctx.lineWidth = 1.2;
          ctx.strokeStyle = "#000000";
          ctx.stroke();
          ctx.restore();
        }
      }
    }
  } else {
    ctx.fillStyle = "#000000";
    centeredText(ctx, "Geen\xa0gegevens\xa0gevonden", canvasWidth / 2, canvasHeight / 2);
  }

  ctx.restore();

  // rand van achtergrond-rechthoek
  // boven- en rechter-rand 0.5 pixel naar binnen geschoven zodat ze niet half worden geclipt
  ctx.beginPath();
  ctx.moveTo(x1, y1 + 0.5);
  ctx.lineTo(x1, y2);
  ctx.lineTo(x2 - 0.5, y2);
  ctx.lineTo(x2 - 0.5, y1 + 0.5);
  ctx.closePath();
  ctx.strokeStyle = "#3466c0";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Klaar met de clip
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  if (w < 2 * r) r = w / 2;
  if (h < 2 * r) r = h / 2;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function centeredText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number): void {
  let leading = 3;
  let ascent = 0;
  let totalHeight = -leading;
  let comps = text.split(" ");
  let sizes = [];
  for (let i = 0; i < comps.length; i++) {
    let ts = ctx.measureText(comps[i]);
    let h = ts.actualBoundingBoxAscent + ts.actualBoundingBoxDescent + leading;
    sizes.push([ts.width, h]);
    totalHeight += h;
    ascent = ts.actualBoundingBoxAscent;
  }
  let yy = y - totalHeight / 2 + ascent;
  for (let i = 0; i < comps.length; i++) {
    ctx.fillText(comps[i], x - sizes[i][0] / 2, yy);
    yy += sizes[i][1];
  }
}

function centeredLabel(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, f1: string, f2: string): void {
  let letter = text.substring(0, 1);
  let suffix = text.substring(1);
  ctx.font = f1;
  let ts1 = ctx.measureText(letter);
  let lh = ts1.actualBoundingBoxAscent + ts1.actualBoundingBoxDescent;
  let loffset = 0;
  if (suffix != "") {
    ctx.font = f2;
    let ts2 = ctx.measureText(suffix);
    loffset = ts2.actualBoundingBoxAscent + ts2.actualBoundingBoxDescent + 5;
    ctx.fillText(suffix, x - ts2.width / 2, y - (lh + loffset) / 2 + ts2.actualBoundingBoxAscent);
    ctx.font = f1;
  }
  ctx.fillText(letter, x - ts1.width / 2, y - (lh + loffset) / 2 + loffset + ts1.actualBoundingBoxAscent);
}
</script>

<template>
  <div class="widget-item">
    <header>
      <h2>Energiekompas</h2>
      <div class="widget-actions">
        <information-dropdown>
          <template v-slot:header>
            <h2>Energiekompas</h2>
          </template>
          <p>
            Het EnergieKompas combineert de krachten van beschikbare middelen en methodes (het energielabel en de WEii-score), en geeft in één oogopslag inzicht in de verduurzamingsroute van een pand.
            Wat is de meest effectieve route om gebouwen te verduurzamen tot een Paris Proof niveau? Veel bestaande wet- en regelgeving en beschikbare instrumenten bieden geen eenduidige oplossing.
            Het EnergieKompas wel. Door het energielabel en de WEii-score (werkelijk gemeten energiegebruik) te combineren, maakt het EnergieKompas voor elk gebouw inzichtelijk wat de meest effectieve
            route is om op het gewenste ambitieniveau te komen.
          </p>
        </information-dropdown>
      </div>
    </header>

    <!--<loader-spinner v-if="isLoading === true" :is-loading="isLoading" :min-height="200"></loader-spinner>-->
    <!-- Charts werken niet als ze in een v-if constructie zitten -->
    <!--<div v-show="isLoading === false && chart.any == true">-->
    <div>
      <div class="widget-wrapper">
        <div class="widget-chart energy-compass" style="position: relative;">
          <div
            v-show="tooltipVisible"
            class="energy-compass-tooltip"
            role="tooltip"
            aria-hidden="false"
            :style="{ left: tooltipX + 'px', top: tooltipY + 'px' }"
            v-html="tooltipHtml"
          ></div>
          <canvas id="energyCompassCanvas" ref="canvasRef" tabindex="0" @click="doClick" @mousemove="doMove" @mouseleave="doLeave" @keydown="onCanvasKeyDown" class="energy-compass-canvas" />
          <div class="legend ml-1">
            <div v-for="(legendItem, index) in legend" :key="index" class="legend-item">
              <v-icon v-if="legendItem.icon" :color="legendItem.color">mdi-{{ legendItem.icon }}</v-icon>
              <span class="ml-0-75">{{ legendItem.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!--
    <div v-show="isLoading === false && chart.any == false">
      <p>Er zijn geen energie-waarden beschikbaar voor dit project</p>
    </div>
    -->
  </div>
</template>

<style scoped>
.widget-item .widget-wrapper {
  margin-top: 6px;
  overflow: visible;
}

.widget-chart.energy-compass {
  max-width: 100%;
  overflow: visible;
  z-index: 1;
}

.energy-compass-tooltip {
  position: absolute;
  z-index: 1000;
  background-color: #ffffff;
  border: 1px solid #000000;
  padding: 1px 5px;
  pointer-events: none;
}

.energy-compass-canvas {
  display: block;
  width: 100%;
  max-width: 100%;
  height: 360px;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  max-width: 100%;
}

.legend-item {
  display: flex;
  align-items: center;
  white-space: nowrap;
}
</style>
